import { Box, Link, Paper, ThemeProvider, Typography } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AllRoutes } from "../../config/routes";
import siteTheme from "../../theme/theme";

const newsTheme = createTheme(siteTheme, {
  palette: {
    mode: "dark",
    background: {
      default: "#000000",
      paper: "#1a1a1a",
    },
    text: {
      primary: "#ffffff",
      secondary: "#e0e0e0",
    },
  },
});

interface NewsItem {
  title: string;
  link: string;
  publishedAt: string;
  image: string;
}

const regions = [
  { id: "world", title: "World", source: "BBC", locale: "en-US" },
  { id: "brazil", title: "Brasil", source: "G1", locale: "pt-BR" },
] as const;

const PAGE_SIZE = 4;
const PAGE_MS = 15000;
const FEED_MS = 2 * 60 * 60 * 1000;

const splitHeadline = (title: string) => {
  const splitAt = title.lastIndexOf(" - ");
  if (splitAt <= 0) {
    return { headline: title, source: "" };
  }
  return {
    headline: title.slice(0, splitAt),
    source: title.slice(splitAt + 3),
  };
};

const NewsWidget: React.FC<{
  region: (typeof regions)[number];
  page: number;
}> = ({ region, page }) => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `/api/public/news?region=${region.id}&t=${Date.now()}`,
          { cache: "no-store" },
        );
        if (!response.ok) {
          throw new Error(`News request failed (${response.status})`);
        }
        const data = (await response.json()) as NewsItem[];
        if (!cancelled) {
          setItems(data);
          setError("");
        }
      } catch (err) {
        console.error("Error fetching news:", err);
        if (!cancelled) {
          setError("Failed to load news.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    const timer = window.setInterval(load, FEED_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [region.id]);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const visible = items.slice(
    (page % pageCount) * PAGE_SIZE,
    (page % pageCount) * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: { xs: "78vh", sm: "100%" },
        backgroundColor: "#1a1a1a",
        backgroundImage: "none",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {region.title}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.6 }}>
          {Array.from({ length: pageCount }, (_, index) => (
            <Box
              key={index}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor:
                  index === page % pageCount
                    ? "#fff"
                    : "rgba(255,255,255,0.28)",
              }}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
          pb: 1,
        }}
      >
        {loading ? (
          <Typography color="text.secondary">Loading news...</Typography>
        ) : null}
        {error ? <Typography color="error">{error}</Typography> : null}
        {!loading && !error && items.length === 0 ? (
          <Typography color="text.secondary">No news available.</Typography>
        ) : null}
        {visible.length > 0 ? (
          <Box
            key={page % pageCount}
            sx={{
              width: "100%",
              maxHeight: "100%",
              aspectRatio: "1 / 1",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: 1,
              animation: "newsFade 400ms ease",
              "@keyframes newsFade": {
                from: { opacity: 0.35 },
                to: { opacity: 1 },
              },
            }}
          >
            {visible.map((item) => {
              const { headline, source } = splitHeadline(item.title);
              const when = new Date(item.publishedAt).toLocaleString(
                region.locale,
                {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                },
              );
              return (
                <Link
                  key={`${item.link}-${item.publishedAt}`}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  color="inherit"
                  sx={{
                    position: "relative",
                    display: "block",
                    minWidth: 0,
                    minHeight: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#111",
                  }}
                >
                  {item.image ? (
                    <Box
                      component="img"
                      src={item.image}
                      alt=""
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.05) 55%)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      p: 1.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.25,
                        fontSize: "clamp(0.78rem, 1.25vw, 1rem)",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {headline}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[source || region.source, when].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                </Link>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
};

const News: React.FC = () => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPage((current) => current + 1);
    }, PAGE_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const head = document.head;
    const manifest = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]',
    );
    const previousManifest = manifest?.getAttribute("href") ?? null;
    const manifestLink = manifest ?? document.createElement("link");
    const createdManifest = !manifest;
    manifestLink.rel = "manifest";
    manifestLink.href = "/news.webmanifest";
    if (createdManifest) {
      head.appendChild(manifestLink);
    }

    const setMeta = (name: string, content: string) => {
      const existing = document.querySelector<HTMLMetaElement>(
        `meta[name="${name}"]`,
      );
      const tag = existing ?? document.createElement("meta");
      const created = !existing;
      const previous = existing?.getAttribute("content") ?? null;
      tag.name = name;
      tag.content = content;
      if (created) {
        head.appendChild(tag);
      }
      return () => {
        if (created) {
          tag.remove();
        } else if (previous === null) {
          tag.removeAttribute("content");
        } else {
          tag.content = previous;
        }
      };
    };

    const restoreMeta = [
      setMeta("apple-mobile-web-app-capable", "yes"),
      setMeta("mobile-web-app-capable", "yes"),
      setMeta("apple-mobile-web-app-status-bar-style", "black-translucent"),
      setMeta("apple-mobile-web-app-title", "News"),
      setMeta("theme-color", "#000000"),
    ];

    const viewport = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );
    const previousViewport = viewport?.getAttribute("content") ?? null;
    if (viewport) {
      viewport.content =
        "width=device-width, initial-scale=1, viewport-fit=cover";
    }

    return () => {
      restoreMeta.forEach((restore) => restore());
      if (createdManifest) {
        manifestLink.remove();
      } else if (previousManifest === null) {
        manifestLink.removeAttribute("href");
      } else {
        manifestLink.href = previousManifest;
      }
      if (viewport && previousViewport !== null) {
        viewport.content = previousViewport;
      }
    };
  }, []);

  return (
    <ThemeProvider theme={newsTheme}>
      <Box
        sx={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#000",
          overflow: { xs: "auto", sm: "hidden" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            minHeight: 64,
            backgroundColor: "#333",
            color: "#ffffff",
          }}
        >
          <Box
            component={RouterLink}
            to={AllRoutes.Home}
            sx={{ display: "flex", alignItems: "center", lineHeight: 0 }}
          >
            <img src="/images/logo.png" alt="Home" style={{ height: 40 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#ffffff" }}
          >
            News
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
            p: 1,
          }}
        >
          {regions.map((region) => (
            <NewsWidget key={region.id} region={region} page={page} />
          ))}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default News;
